import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useIsPresent } from 'framer-motion';

// Start downloading the 3D code as soon as the site loads (in idle time), so it's ready instantly
const loadScene = () => import('./HeroScene');
const HeroScene = lazy(loadScene);

// Phones, data-saver and low-memory devices get the light CSS version instead of the ~220 KB 3D engine
const lightMode = () => {
  if (typeof window === 'undefined') return true;
  const c = navigator.connection || {};
  return window.matchMedia('(max-width: 640px)').matches || c.saveData || /(^|-)2g$/.test(c.effectiveType || '') || (navigator.deviceMemory && navigator.deviceMemory < 4);
};
if (typeof window !== 'undefined' && !lightMode()) {
  // download the 3D code after the page has finished loading, in idle time
  const idle = () => (window.requestIdleCallback || ((f) => setTimeout(f, 300)))(() => { loadScene(); });
  if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
}

function hasWebGL() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch { return false; }
}

/* CSS-only isometric blocks shown while 3D loads or if WebGL is unavailable */
export function IsoFallback() {
  return (
    <div className="iso-fallback" aria-hidden="true">
      {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className={`iso-cube c${i}`}><i /><i /><i /></span>)}
    </div>
  );
}

export default function Hero3D() {
  const [ok, setOk] = useState(null);
  const [visible, setVisible] = useState(true);
  const ref = useRef(null);
  const isPresent = useIsPresent(); // false while the page is animating out

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setOk(hasWebGL() && !reduce && !lightMode());
  }, []);

  // Stop rendering when the hero scrolls out of view
  useEffect(() => {
    if (!ref.current) return undefined;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ok]);

  return (
    <div ref={ref} style={{ width: '100%', height: '100%' }}>
      {ok ? (
        <Suspense fallback={<IsoFallback />}>
          <HeroScene active={visible && isPresent} />
        </Suspense>
      ) : <IsoFallback />}
    </div>
  );
}
